/**
 * useAutoDepositNotifications - Hook for detecting and notifying about auto-deposit events
 *
 * Features:
 * - Polls activity feed for new auto_deposit activities
 * - Shows toast notification when auto-deposit is detected
 * - Triggers dashboard refresh after auto-deposit
 * - Prevents duplicate notifications
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import apiService, { Activity } from '../services/api';

interface UseAutoDepositNotificationsOptions {
  enabled?: boolean;
  pollInterval?: number; // in milliseconds
  onAutoDeposit?: (activity: Activity) => void;
}

interface UseAutoDepositNotificationsResult {
  lastAutoDeposit: Activity | null;
  isPolling: boolean;
  error: string | null;
}

const DEFAULT_POLL_INTERVAL = 30000; // 30 seconds

export const useAutoDepositNotifications = (
  options: UseAutoDepositNotificationsOptions = {}
): UseAutoDepositNotificationsResult => {
  const {
    enabled = true,
    pollInterval = DEFAULT_POLL_INTERVAL,
    onAutoDeposit,
  } = options;

  const { autoDeposit: showAutoDepositToast } = useToast();
  const [lastAutoDeposit, setLastAutoDeposit] = useState<Activity | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the ID of the last seen auto_deposit activity to prevent duplicates
  const lastSeenIdRef = useRef<number | null>(null);
  // Track if this is the initial load (don't show toast for old activities)
  const isInitialLoadRef = useRef(true);
  // Store callbacks in refs to prevent infinite re-render loops
  const onAutoDepositRef = useRef(onAutoDeposit);
  const showAutoDepositToastRef = useRef(showAutoDepositToast);

  // Update refs when callbacks change (without triggering effect re-runs)
  useEffect(() => {
    onAutoDepositRef.current = onAutoDeposit;
    showAutoDepositToastRef.current = showAutoDepositToast;
  }, [onAutoDeposit, showAutoDepositToast]);

  const checkForAutoDeposits = useCallback(async () => {
    if (!enabled) return;

    try {
      setIsPolling(true);
      setError(null);

      // Fetch recent activities, filtering for auto_deposit type
      const response = await apiService.activities.getList({
        type: 'auto_deposit',
        limit: 1,
      });

      if (response.activities && response.activities.length > 0) {
        const latestActivity = response.activities[0];

        // Check if this is a new auto_deposit we haven't seen
        if (
          latestActivity.id !== lastSeenIdRef.current &&
          !isInitialLoadRef.current
        ) {
          // New auto_deposit detected!
          setLastAutoDeposit(latestActivity);

          // Show toast notification
          const details = latestActivity.details as { lp_amount?: number; drift_amount?: number } | undefined;
          const lpAmount = details?.lp_amount || 0;
          const driftAmount = details?.drift_amount || 0;
          const totalAmount = latestActivity.amount || (lpAmount + driftAmount);

          showAutoDepositToastRef.current(totalAmount, lpAmount, driftAmount);

          // Trigger callback if provided (use ref to avoid dependency issues)
          if (onAutoDepositRef.current) {
            onAutoDepositRef.current(latestActivity);
          }
        }

        // Update the last seen ID
        lastSeenIdRef.current = latestActivity.id;
      }

      // Mark initial load as complete
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } catch (err) {
      console.error('Failed to check for auto-deposits:', err);
      setError('Failed to check for auto-deposits');
    } finally {
      setIsPolling(false);
    }
  }, [enabled]);

  // Set up polling interval
  useEffect(() => {
    if (!enabled) return;

    // Initial check
    checkForAutoDeposits();

    // Set up interval for periodic checks
    const interval = setInterval(checkForAutoDeposits, pollInterval);

    return () => {
      clearInterval(interval);
    };
  }, [enabled, pollInterval, checkForAutoDeposits]);

  // Reset initial load flag when component remounts
  useEffect(() => {
    return () => {
      isInitialLoadRef.current = true;
    };
  }, []);

  return {
    lastAutoDeposit,
    isPolling,
    error,
  };
};

export default useAutoDepositNotifications;
